/**
 * Responsive chart sizing + theme detection (Phase 3, §6.4) — SPINE.
 *
 * `useChartDims` measures its container with a `ResizeObserver` and derives a
 * viewBox width/height, so every chart is a fluid SVG with no fixed pixel size.
 * `useChartMode` resolves the active light/dark theme from the app theme toggle
 * (`:root[data-theme]`) first, falling back to `prefers-color-scheme`, and keeps
 * it live via a `MutationObserver` + media-query listener.
 */
import { useEffect, useRef, useState, type RefObject } from 'react';
import type { ChartMode } from './palette.ts';

export interface ChartDims {
  width: number;
  height: number;
}

export interface UseChartDimsOptions {
  /** Height as a fraction of measured width when `height` is not given. */
  aspect?: number;
  /** Fixed height in px (overrides `aspect`). */
  height?: number;
  /** Floor for the measured width, so a chart never collapses below this. */
  minWidth?: number;
}

export interface UseChartDimsResult {
  ref: RefObject<HTMLDivElement | null>;
  dims: ChartDims;
}

/** Measure a container and return a responsive `{ width, height }` viewBox. */
export function useChartDims(options: UseChartDimsOptions = {}): UseChartDimsResult {
  const { aspect = 0.5, height, minWidth = 280 } = options;
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(640);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const measured = entries[0]?.contentRect.width ?? 0;
      if (measured > 0) setWidth(Math.max(Math.round(measured), minWidth));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [minWidth]);

  const resolvedHeight = height ?? Math.round(width * aspect);
  return { ref, dims: { width, height: resolvedHeight } };
}

/** Read the active theme: explicit `data-theme` wins, else `prefers-color-scheme`. */
function readChartMode(): ChartMode {
  if (typeof document !== 'undefined') {
    const attr = document.documentElement.getAttribute('data-theme');
    if (attr === 'dark') return 'dark';
    if (attr === 'light') return 'light';
  }
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

/** Live light/dark mode for entity/semantic colour resolution. */
export function useChartMode(): ChartMode {
  const [mode, setMode] = useState<ChartMode>(readChartMode);

  useEffect(() => {
    const update = () => setMode(readChartMode());
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    media.addEventListener('change', update);
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    return () => {
      media.removeEventListener('change', update);
      observer.disconnect();
    };
  }, []);

  return mode;
}
